import type { ComponentType } from "react";
import { useSearchParams } from "react-router";
import { useTalentWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { today as todayIn } from "../../../lib/format";
import { useViewerZone } from "../../../lib/zeitzone";
import { useLang } from "../../creator/sprache";
import { FadenZeile, ThreadPanel, useChatSeitenHoehe } from "./chat";
import { useChatSenders, useCreatorThread } from "./hooks";
import { countLabel, previewText, shortWhen } from "./model";
import "./nachrichten.css";

/**
 * Deine Nachrichten (12_app_creator/chat.html): der eine Gesprächsfaden des Creators mit seiner Agentur (TA-110 … TA-117).
 * Nachrichten anderer Agenturen desselben Kontos erscheinen nie hier; sie stehen nach dem Agenturwechsel oben im Kopf.
 */
export function CreatorNachrichten() {
  // K-517: „Deiner Agentur schreiben“ aus einem Deal bzw. einer Rechnung belegt das Objekt vor (?objekt=deal:<id>)
  const vorbelegt = useSearchParams()[0].get("objekt");
  const ws = useTalentWorkspace();
  const orgId = ws.link.org_id;
  const talentId = ws.link.talent_id;
  // Zeiten in der Zone der betrachtenden Person, wie Kalender und Glocke (Befund 12)
  const timeZone = useViewerZone(ws.org.timezone);
  const today = todayIn(timeZone);
  const thread = useCreatorThread(orgId, talentId);
  const senders = useChatSenders(orgId, talentId);
  const lang = useLang();
  useChatSeitenHoehe();
  const t = (de: string, en: string) => (lang === "en" ? en : de);

  if (thread.error) return <ErrorState error={thread.error} retry={() => void thread.refetch()} />;
  if (thread.isPending) return <Loading />;
  const row = thread.data;
  if (!row) return <NoAccess text={t("Dein Zugang zu dieser Agentur ist nicht aktiv. Wende dich an deine Agentur.", "Your access to this agency is not active. Please contact your agency.")} />;

  const others = ws.others.filter((o) => o.org_id !== orgId);
  return (
    <main id="main" className="content tk-cr tk-cr-chat tk-chat-seite tk-chat-open">
      {/* D-1 S6: wie die Chatseite der Agentur: schmaler Kopf, Liste und Verlauf in voller Höhe (am Handy direkt der Verlauf) */}
      <div className="tk-chat-seitenkopf">
        <h1>{t("Deine Nachrichten", "Your messages")}</h1>
        <p>{row.unread > 0
          ? (lang === "en" ? `${countLabel(row.unread, "new message", "new messages")} from ${ws.org.name}` : `${countLabel(row.unread, "neue Nachricht", "neue Nachrichten")} von ${ws.org.name}`)
          : t(`Dein direkter Draht zu ${ws.org.name}.`, `Your direct line to ${ws.org.name}.`)}</p>
        {api.mode === "demo" && <small className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictitious.")}</small>}
      </div>
      <div className="tk-chat-rahmen">
        <section className="tk-chat-list" data-coco="chat-liste" aria-labelledby="unterhaltungen">
          <h3 id="unterhaltungen" className="tk-chat-list-titel">{t("Unterhaltungen", "Conversations")}</h3>
          <div className="tk-chat-list-zeilen">
            <FadenZeile name={ws.org.name} to="/creator/nachrichten" current unread={row.unread}
              when={row.last_at ? shortWhen(row.last_at, today, timeZone, lang) : null}
              preview={previewText(row.last_body, row.last_side, "talent", 90, lang)} />
            {others.length > 0 && (
              <small className="tk-chat-list-leer">{t(`Deine Nachrichten mit ${others.map((o) => o.name).join(", ")} siehst du, wenn du oben die Agentur wechselst.`,
                `You can see your messages with ${others.map((o) => o.name).join(", ")} when you switch agency at the top.`)}</small>
            )}
          </div>
        </section>
        <ThreadPanel key={talentId} portal="creator" orgId={orgId} talentId={talentId} title={ws.org.name} timeZone={timeZone} talentName={ws.talentName} vorbelegt={vorbelegt}
          senderName={(m) => (m.sender_side === "agency" ? senders.data?.find((x) => x.user_id === m.sender_user_id)?.display_name ?? null : null)} />
      </div>
    </main>
  );
}

/**
 * Seite je Talent-Datensatz neu aufbauen: ein Agenturwechsel verwirft Entwürfe und Zwischenstände der vorigen Agentur
 * (Regel aus dem Rechte-Modell). Lokale Fassung des Bausteins aus dem Creator-Portal.
 */
export function CreatorScoped({ page: Page }: { page: ComponentType }) {
  const ws = useTalentWorkspace();
  return <Page key={ws.link.talent_id} />;
}
